import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, MapPin, Clock, Church, Bell, X, Check, Smartphone, Plus, Users, Loader2, Pencil, Trash2, ChevronLeft, ChevronRight, ExternalLink, Eye, EyeOff, Share2 } from "lucide-react";
import { getMyRsvps, getMyCreatedEvents, createEvent, updateRsvpReminder, cancelRsvp, deleteEvent, getLiturgicalCalendar } from "@/lib/api";
import { toast } from "sonner";
import { LITURGICAL_SEASONS, getLiturgicalSeason } from "@/lib/liturgicalCalendar";

const LITURGICAL_COLOR_HEX = {
  green: "#2E7D32", white: "#D4AF37", violet: "#6B3FA0", red: "#C62828", rose: "#C48793", black: "#1a1a1a",
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function bibleGatewayUrl(ref) {
  if (!ref) return null;
  return `https://www.biblegateway.com/passage/?search=${encodeURIComponent(ref)}&version=RSVCE`;
}

// Easter/liturgical date calculations
function calculateEaster(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}
function getAshWednesday(y) { const e = calculateEaster(y); const d = new Date(e); d.setDate(e.getDate() - 46); return d; }
function getPentecost(y) { const e = calculateEaster(y); const d = new Date(e); d.setDate(e.getDate() + 49); return d; }
function getAdventStart(y) { const c = new Date(y, 11, 25); const dow = c.getDay(); const sub = dow === 0 ? 28 : (dow + 21); const a = new Date(c); a.setDate(c.getDate() - sub); return a; }
function getBaptismOfLord(y) { const ep = new Date(y, 0, 6); if (ep.getDay() === 0) return new Date(y, 0, 7); const d = new Date(ep); d.setDate(ep.getDate() + (7 - ep.getDay())); return d; }

function LiturgicalProgressTracker() {
  const today = new Date();
  const year = today.getFullYear();
  const adventStartThisYear = getAdventStart(year);
  const adventStartLastYear = getAdventStart(year - 1);
  const adventStartNextYear = getAdventStart(year + 1);
  const litYearStart = today >= adventStartThisYear ? adventStartThisYear : adventStartLastYear;
  const litYearEnd = today >= adventStartThisYear ? adventStartNextYear : adventStartThisYear;
  const totalDays = Math.round((litYearEnd - litYearStart) / 86400000);
  const elapsedDays = Math.round((today - litYearStart) / 86400000);
  const progressPercent = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));

  const litYear = today >= adventStartThisYear ? year : year - 1;
  const easterDate = calculateEaster(litYear + 1);
  const ashWed = getAshWednesday(litYear + 1);
  const pentecostDate = getPentecost(litYear + 1);
  const baptismDate = getBaptismOfLord(litYear + 1);
  const christmasDate = new Date(litYear, 11, 25);
  const holyThursdayDate = new Date(easterDate); holyThursdayDate.setDate(easterDate.getDate() - 3);
  const pentecostEnd = new Date(pentecostDate); pentecostEnd.setDate(pentecostDate.getDate() + 1);

  const segments = [
    { name: "Advent", color: LITURGICAL_SEASONS.ADVENT.colors.primary, start: litYearStart, end: new Date(litYear, 11, 24, 23, 59) },
    { name: "Christmas", color: LITURGICAL_SEASONS.CHRISTMAS.colors.primary, start: christmasDate, end: baptismDate },
    { name: "Ordinary Time", color: LITURGICAL_SEASONS.ORDINARY_TIME.colors.primary, start: baptismDate, end: ashWed },
    { name: "Lent", color: LITURGICAL_SEASONS.LENT.colors.primary, start: ashWed, end: holyThursdayDate },
    { name: "Triduum", color: LITURGICAL_SEASONS.EASTER.colors.primary, start: holyThursdayDate, end: easterDate },
    { name: "Easter", color: "#D4A843", start: easterDate, end: pentecostDate },
    { name: "Pentecost", color: LITURGICAL_SEASONS.PENTECOST.colors.primary, start: pentecostDate, end: pentecostEnd },
    { name: "Ordinary Time", color: LITURGICAL_SEASONS.ORDINARY_TIME.colors.primary, start: pentecostEnd, end: litYearEnd },
  ];

  const segmentData = segments.map(seg => ({ ...seg, pct: (Math.max(1, Math.round((seg.end - seg.start) / 86400000)) / totalDays) * 100 }));
  const currentSegment = segmentData.find(s => today >= s.start && today < s.end);
  const daysLeft = currentSegment ? Math.max(0, Math.round((currentSegment.end - today) / 86400000)) : 0;

  return (
    <Card className="border-[#E5E0D5] bg-white overflow-hidden mb-4">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26]">Liturgical Year</h2>
            <p className="font-['Nunito'] text-xs text-[#6B665E]">
              {Math.round(progressPercent)}% complete — {daysLeft} day{daysLeft !== 1 ? 's' : ''} left in {currentSegment?.name || "this season"}
            </p>
          </div>
        </div>
        <div className="relative w-full h-5 rounded-full overflow-hidden flex bg-[#F0EBE0]">
          {segmentData.map((seg, i) => (
            <div key={i} className="h-full relative group transition-all" style={{ width: `${seg.pct}%`, backgroundColor: seg.color }}>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[#2D2A26] text-white text-[10px] font-['Nunito'] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">{seg.name}</div>
            </div>
          ))}
          <div className="absolute top-0 h-full w-0.5 bg-[#2D2A26] z-10" style={{ left: `${progressPercent}%` }}>
            <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#2D2A26] border-2 border-white shadow" />
            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#2D2A26] border-2 border-white shadow" />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
          {segmentData.filter((s, i, arr) => i === arr.findIndex(a => a.name === s.name)).map((seg, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
              <span className={`font-['Nunito'] text-[10px] ${today >= seg.start && today < seg.end ? 'font-bold text-[#2D2A26]' : 'text-[#6B665E]'}`}>{seg.name}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function LiturgicalCalendarTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);

  useEffect(() => {
    setLoading(true);
    getLiturgicalCalendar(year, month)
      .then(res => setEntries(res.data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [year, month]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  return (
    <div>
      {/* Progress Tracker */}
      <LiturgicalProgressTracker />

      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
        <h2 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">{MONTHS[month - 1]} {year}</h2>
        <Button variant="ghost" size="sm" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" /></div>
      ) : entries.length === 0 ? (
        <Card className="border-[#E5E0D5]">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-[#6B665E] mb-4" />
            <p className="text-[#6B665E] font-['Nunito']">No liturgical calendar data for {MONTHS[month - 1]} {year}.</p>
            <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">Ask your site admin to upload calendar data.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5">
          {entries.map(e => {
            const isToday = e.date === todayStr;
            const isExpanded = expandedDay === e.date;
            const dayNum = parseInt(e.date.split("-")[2]);
            const dow = dayNames[new Date(e.date + "T12:00:00").getDay()];
            const colorHex = LITURGICAL_COLOR_HEX[e.color] || "#6B665E";
            const isHolyDay = e.notes && e.notes.toLowerCase().includes("holyday of obligation");

            return (
              <div key={e.date}
                className={`rounded-lg border transition-all ${
                  isHolyDay ? "border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/5 ring-1 ring-[var(--liturgical-primary)]/20" :
                  isToday ? "border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/5" :
                  e.rank === "Solemnity" ? "border border-[#E5E0D5] bg-[#F5F0E6]" :
                  e.rank === "Feast" ? "border border-[#E5E0D5] bg-[#F8F5ED]" :
                  "border-[#E5E0D5] bg-white"
                }`}
                style={(e.rank === "Solemnity" || e.rank === "Feast") && !isHolyDay && !isToday ? { borderLeftWidth: "4px", borderLeftColor: colorHex } : {}}
              >
                <button
                  onClick={() => setExpandedDay(isExpanded ? null : e.date)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                  data-testid={`liturgical-${e.date}`}
                >
                  <div className="flex-shrink-0 w-10 text-center">
                    <div className={`text-xs font-['Nunito'] ${isToday ? "font-bold text-[var(--liturgical-primary)]" : "text-[#9C9689]"}`}>{dow}</div>
                    <div className={`text-lg font-['Crimson_Text'] font-bold leading-tight ${isToday ? "text-[var(--liturgical-primary)]" : "text-[#2D2A26]"}`}>{dayNum}</div>
                  </div>
                  <div className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorHex }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {(e.rank === "Solemnity" || e.rank === "Feast") ? (
                        <p className={`text-sm font-['Nunito'] text-[#2D2A26] truncate ${e.rank === "Solemnity" ? "font-bold" : "font-semibold"}`}>
                          <span className="text-[10px] uppercase tracking-wide text-[#6B665E] font-semibold">{e.rank}: </span>
                          {e.celebration}
                        </p>
                      ) : (
                        <p className="text-sm font-['Nunito'] font-medium text-[#2D2A26] truncate">{e.celebration}</p>
                      )}
                      {isHolyDay && (
                        <Badge className="bg-[var(--liturgical-primary)] text-white text-[9px] font-['Nunito'] px-1.5 py-0 flex-shrink-0">
                          Holy Day
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {e.rank === "Memorial" && (
                        <span className="text-[10px] font-['Nunito'] text-[#6B665E]">Memorial</span>
                      )}
                      {e.season && (
                        <span className="text-[10px] font-['Nunito'] text-[#9C9689]">{e.season}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-[#9C9689] flex-shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 pt-0 ml-[52px] border-t border-[#E5E0D5] mt-0">
                    <div className="pt-2 space-y-2">
                      {e.notes && <p className="text-xs text-[#6B665E] font-['Nunito'] italic">{e.notes}</p>}
                      {e.readings && Object.keys(e.readings).length > 0 && (
                        <div className="space-y-1">
                          <h4 className="text-xs font-semibold font-['Nunito'] text-[#2D2A26]">Readings (RSVCE)</h4>
                          {e.readings.first && (
                            <a href={bibleGatewayUrl(e.readings.first)} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs font-['Nunito'] text-[var(--liturgical-primary)] hover:underline">
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />First: {e.readings.first}
                            </a>
                          )}
                          {e.readings.psalm && (
                            <a href={bibleGatewayUrl(e.readings.psalm)} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs font-['Nunito'] text-[var(--liturgical-primary)] hover:underline">
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />Psalm: {e.readings.psalm}
                            </a>
                          )}
                          {e.readings.second && (
                            <a href={bibleGatewayUrl(e.readings.second)} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs font-['Nunito'] text-[var(--liturgical-primary)] hover:underline">
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />Second: {e.readings.second}
                            </a>
                          )}
                          {e.readings.gospel && (
                            <a href={bibleGatewayUrl(e.readings.gospel)} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs font-['Nunito'] text-[var(--liturgical-primary)] hover:underline">
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />Gospel: {e.readings.gospel}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RsvpsTab({ upcomingRsvps, pastRsvps, navigate, RsvpCard }) {
  const [showPast, setShowPast] = useState(false);

  return (
    <div>
      {upcomingRsvps.length === 0 && pastRsvps.length === 0 ? (
        <Card className="border-[#E5E0D5]">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-[#6B665E] mb-4" />
            <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">No RSVPs</h3>
            <p className="text-[#6B665E] font-['Nunito'] mb-4">You haven't RSVP'd to any events yet.</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">Browse Events</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {upcomingRsvps.length > 0 ? (
            upcomingRsvps.map(rsvp => <RsvpCard key={rsvp.rsvp_id} rsvp={rsvp} />)
          ) : (
            <Card className="border-[#E5E0D5]">
              <CardContent className="py-8 text-center">
                <p className="text-[#6B665E] font-['Nunito'] text-sm">No upcoming RSVPs</p>
              </CardContent>
            </Card>
          )}

          {pastRsvps.length > 0 && (
            <div>
              <Button variant="ghost" size="sm" onClick={() => setShowPast(!showPast)}
                className="font-['Nunito'] text-xs text-[#6B665E] hover:text-[#2D2A26]" data-testid="toggle-past-rsvps">
                {showPast ? <EyeOff className="w-3 h-3 mr-1.5" /> : <Eye className="w-3 h-3 mr-1.5" />}
                {showPast ? "Hide" : "Show"} Past RSVPs ({pastRsvps.length})
              </Button>
              {showPast && (
                <div className="space-y-4 mt-2">
                  {pastRsvps.map(rsvp => <RsvpCard key={rsvp.rsvp_id} rsvp={rsvp} isPast />)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const REMINDER_OPTIONS = [
  { value: "1d", label: "1 day before" },
  { value: "2h", label: "2 hours before" },
  { value: "1h", label: "1 hour before" },
  { value: "30m", label: "30 minutes before" },
];

function CreateEventDialog({ user, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleSubmit = async () => {
    if (!title || !description || !eventDate || !location) {
      toast.error("Fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const [hours, minutes] = eventTime.split(':').map(Number);
      const combinedDate = new Date(eventDate);
      combinedDate.setHours(hours, minutes, 0, 0);

      await createEvent({
        title, description,
        event_date: combinedDate.toISOString(),
        location,
        parish_id: user.parish_id,
        privacy,
        created_via: "user"
      });
      toast.success("Event created!");
      setOpen(false);
      setTitle(""); setDescription(""); setEventDate(""); setEventTime("10:00"); setLocation(""); setPrivacy("public");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create event");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold text-sm" data-testid="create-event-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Create Event
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">Create Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Title *</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Event title" className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="event-title-input" />
          </div>
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Description *</label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What's this event about?" className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[80px]" data-testid="event-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Date *</label>
              <Input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="event-date-input" min={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="space-y-1.5">
              <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Time</label>
              <Input type="time" value={eventTime} onChange={e => setEventTime(e.target.value)} className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="event-time-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Location *</label>
            <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="Where is the event?" className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="event-location-input" />
          </div>
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Privacy</label>
            <Select value={privacy} onValueChange={setPrivacy}>
              <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="event-privacy-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="public" className="font-['Nunito']">Public</SelectItem>
                <SelectItem value="private" className="font-['Nunito']">Private</SelectItem>
                <SelectItem value="parish_only" className="font-['Nunito']">Parish Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1 border-[#E5E0D5] font-['Nunito']">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="submit-event-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Event"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MyRsvpsPage({ user }) {
  const navigate = useNavigate();
  const [rsvps, setRsvps] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRsvp, setSelectedRsvp] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [updatingReminder, setUpdatingReminder] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(false);

  const userHasPhone = user?.phone && user.phone.length > 0;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rsvpRes, eventsRes] = await Promise.all([
        getMyRsvps(),
        getMyCreatedEvents()
      ]);
      setRsvps(rsvpRes.data.rsvps || []);
      setMyEvents(eventsRes.data.events || []);
    } catch (err) {
      toast.error("Failed to load events data");
    }
    setLoading(false);
  };

  const handleReminderChange = async (rsvp, timeValue, isChecked) => {
    setUpdatingReminder(rsvp.rsvp_id);
    try {
      const currentTimes = rsvp.reminder_times || [];
      const newTimes = isChecked
        ? [...currentTimes, timeValue]
        : currentTimes.filter(t => t !== timeValue);

      await updateRsvpReminder(rsvp.rsvp_id, newTimes, rsvp.sms_enabled || false);
      setRsvps(prev => prev.map(r =>
        r.rsvp_id === rsvp.rsvp_id ? { ...r, reminder_times: newTimes } : r
      ));

      const option = REMINDER_OPTIONS.find(o => o.value === timeValue);
      toast.success(isChecked ? `Reminder set for ${option?.label}` : `Reminder removed for ${option?.label}`);
    } catch {
      toast.error("Failed to update reminder");
    }
    setUpdatingReminder(null);
  };

  const handleSmsToggle = async (rsvp, enabled) => {
    setUpdatingReminder(rsvp.rsvp_id);
    try {
      await updateRsvpReminder(rsvp.rsvp_id, rsvp.reminder_times || [], enabled);
      setRsvps(prev => prev.map(r =>
        r.rsvp_id === rsvp.rsvp_id ? { ...r, sms_enabled: enabled } : r
      ));
      toast.success(enabled ? "SMS reminders enabled" : "SMS reminders disabled");
    } catch {
      toast.error("Failed to update SMS setting");
    }
    setUpdatingReminder(null);
  };

  const handleCancelRsvp = async () => {
    if (!selectedRsvp) return;
    setCanceling(true);
    try {
      await cancelRsvp(selectedRsvp.rsvp_id);
      setRsvps(prev => prev.filter(r => r.rsvp_id !== selectedRsvp.rsvp_id));
      toast.success("RSVP cancelled");
      setCancelDialogOpen(false);
      setSelectedRsvp(null);
    } catch {
      toast.error("Failed to cancel RSVP");
    }
    setCanceling(false);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    setDeletingEvent(true);
    try {
      await deleteEvent(selectedEvent.event_id);
      setMyEvents(prev => prev.filter(e => e.event_id !== selectedEvent.event_id));
      toast.success("Event deleted");
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete event");
    }
    setDeletingEvent(false);
  };

  const now = new Date();
  const upcomingRsvps = rsvps.filter(r => new Date(r.event_date) >= now);
  const pastRsvps = rsvps.filter(r => new Date(r.event_date) < now);
  const upcomingMyEvents = myEvents.filter(e => new Date(e.event_date) >= now);
  const pastMyEvents = myEvents.filter(e => new Date(e.event_date) < now);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  const EventCard = ({ event }) => {
    const isPast = new Date(event.event_date) < now;
    return (
      <Card className="border-[#E5E0D5] hover:shadow-md transition-shadow" data-testid={`my-event-${event.event_id}`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={isPast ? "bg-gray-100 text-gray-600 border-gray-200" : "bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] border-[var(--liturgical-primary)]/20"}>
                  {event.privacy === "parish_only" ? "Parish Only" : event.privacy === "private" ? "Private" : "Public"}
                </Badge>
                {!isPast && (
                  <Badge variant="outline" className="text-xs border-[#E5E0D5]">
                    {(() => {
                      const diffDays = Math.ceil((new Date(event.event_date) - now) / (1000 * 60 * 60 * 24));
                      if (diffDays === 0) return "Today";
                      if (diffDays === 1) return "Tomorrow";
                      if (diffDays <= 7) return `In ${diffDays} days`;
                      return `In ${Math.ceil(diffDays / 7)} weeks`;
                    })()}
                  </Badge>
                )}
              </div>
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2 line-clamp-2">
                {event.title}
              </h3>
              <div className="space-y-1.5 text-sm text-[#6B665E] font-['Nunito']">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span>{formatDate(event.event_date)} {formatTime(event.event_date)}</span>
                </div>
                {event.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="line-clamp-1">{event.location}</span>
                  </div>
                )}
                {event.parish_name && (
                  <div className="flex items-center gap-2">
                    <Church className="w-4 h-4 flex-shrink-0 text-[var(--liturgical-primary)]" />
                    <span>{event.parish_name}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 mt-3 text-xs text-[#6B665E] font-['Nunito']">
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> {event.going_count || 0} going
                </span>
                <span>{event.maybe_count || 0} maybe</span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                onClick={() => { setSelectedEvent(event); setDeleteDialogOpen(true); }}
                data-testid={`delete-event-${event.event_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const RsvpCard = ({ rsvp, isPast = false }) => {
    const reminderTimes = rsvp.reminder_times || [];
    const remindersSent = rsvp.reminders_sent || [];

    return (
      <Card className="border-[#E5E0D5] hover:shadow-md transition-shadow" data-testid={`rsvp-card-${rsvp.rsvp_id}`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant={rsvp.status === "going" ? "default" : "secondary"}
                  className={rsvp.status === "going" ? "bg-green-100 text-green-700 border-green-200" : "bg-amber-100 text-amber-700 border-amber-200"}
                >
                  {rsvp.status === "going" ? "Going" : "Maybe"}
                </Badge>
                {!isPast && (
                  <Badge variant="outline" className="text-xs border-[#E5E0D5]">
                    {(() => {
                      const diffDays = Math.ceil((new Date(rsvp.event_date) - now) / (1000 * 60 * 60 * 24));
                      if (diffDays === 0) return "Today";
                      if (diffDays === 1) return "Tomorrow";
                      if (diffDays <= 7) return `In ${diffDays} days`;
                      return `In ${Math.ceil(diffDays / 7)} weeks`;
                    })()}
                  </Badge>
                )}
              </div>
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2 line-clamp-2">
                {rsvp.event_title}
              </h3>
              <div className="space-y-1.5 text-sm text-[#6B665E] font-['Nunito']">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span>{formatDate(rsvp.event_date)}</span>
                </div>
                {rsvp.event_location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="line-clamp-1">{rsvp.event_location}</span>
                  </div>
                )}
                {rsvp.parish_name && (
                  <div className="flex items-center gap-2">
                    <Church className="w-4 h-4 flex-shrink-0 text-[var(--liturgical-primary)]" />
                    <button
                      onClick={() => navigate(`/parish/${rsvp.parish_id}`)}
                      className="hover:underline text-[var(--liturgical-primary)]"
                    >
                      {rsvp.parish_name}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {!isPast && (
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                onClick={() => { setSelectedRsvp(rsvp); setCancelDialogOpen(true); }}
                data-testid={`cancel-rsvp-${rsvp.rsvp_id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Reminder Settings - only for upcoming */}
          {!isPast && (
            <div className="mt-4 pt-4 border-t border-[#E5E0D5]">
              <p className="text-xs font-['Nunito'] text-[#6B665E] mb-3 flex items-center gap-1">
                <Bell className="w-3 h-3" /> Email Reminders
              </p>
              <div className="grid grid-cols-2 gap-2">
                {REMINDER_OPTIONS.map((option) => {
                  const isChecked = reminderTimes.includes(option.value);
                  const isSent = remindersSent.includes(option.value);
                  const isUpdating = updatingReminder === rsvp.rsvp_id;

                  return (
                    <div
                      key={option.value}
                      className={`flex items-center gap-2 p-2 rounded-md border ${isChecked ? 'border-[var(--liturgical-primary)] bg-[#F9F7F2]' : 'border-[#E5E0D5]'} ${isSent ? 'opacity-60' : ''}`}
                    >
                      <Checkbox
                        id={`reminder-${rsvp.rsvp_id}-${option.value}`}
                        checked={isChecked}
                        onCheckedChange={(checked) => handleReminderChange(rsvp, option.value, checked)}
                        disabled={isUpdating || isSent}
                        data-testid={`reminder-${option.value}-${rsvp.rsvp_id}`}
                        className="data-[state=checked]:bg-[var(--liturgical-primary)] data-[state=checked]:border-[var(--liturgical-primary)]"
                      />
                      <label
                        htmlFor={`reminder-${rsvp.rsvp_id}-${option.value}`}
                        className="text-sm font-['Nunito'] text-[#2D2A26] cursor-pointer flex items-center gap-1"
                      >
                        {option.label}
                        {isSent && (
                          <span className="text-xs text-green-600 flex items-center gap-0.5">
                            <Check className="w-3 h-3" /> Sent
                          </span>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>

              {/* SMS Toggle */}
              {reminderTimes.length > 0 && (
                <div className="mt-4 pt-3 border-t border-[#E5E0D5]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-[#6B665E]" />
                      <span className="text-sm font-['Nunito'] text-[#2D2A26]">Also send SMS reminders</span>
                    </div>
                    {userHasPhone ? (
                      <Switch
                        checked={rsvp.sms_enabled || false}
                        onCheckedChange={(checked) => handleSmsToggle(rsvp, checked)}
                        disabled={updatingReminder === rsvp.rsvp_id}
                        data-testid={`sms-toggle-${rsvp.rsvp_id}`}
                      />
                    ) : (
                      <Button variant="outline" size="sm" className="text-xs border-[#E5E0D5]" onClick={() => navigate("/settings")}>
                        Add phone number
                      </Button>
                    )}
                  </div>
                  {userHasPhone && rsvp.sms_enabled && (
                    <p className="text-xs text-[#6B665E] mt-1 ml-6">SMS will be sent to {user?.phone}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[#E5E0D5] rounded w-48" />
          <div className="h-32 bg-[#E5E0D5] rounded" />
          <div className="h-32 bg-[#E5E0D5] rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6" data-testid="my-events-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Crimson_Text'] text-2xl md:text-3xl font-bold text-[#2D2A26]">
            Calendar
          </h1>
          <p className="text-[#6B665E] font-['Nunito'] mt-1">
            Liturgical calendar, events, and RSVPs
          </p>
        </div>
        <CreateEventDialog user={user} onCreated={loadData} />
      </div>

      <Tabs defaultValue="liturgical" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6 bg-[#F0EBE0]">
          <TabsTrigger value="liturgical" className="font-['Nunito'] data-[state=active]:bg-white text-xs sm:text-sm" data-testid="liturgical-tab">
            Liturgical
          </TabsTrigger>
          <TabsTrigger value="events" className="font-['Nunito'] data-[state=active]:bg-white text-xs sm:text-sm" data-testid="events-tab">
            My Events ({myEvents.length})
          </TabsTrigger>
          <TabsTrigger value="rsvps" className="font-['Nunito'] data-[state=active]:bg-white text-xs sm:text-sm" data-testid="rsvps-tab">
            RSVPs ({upcomingRsvps.length})
          </TabsTrigger>
        </TabsList>

        {/* Liturgical Calendar Tab */}
        <TabsContent value="liturgical">
          <LiturgicalCalendarTab />
        </TabsContent>

        {/* My Events Tab */}
        <TabsContent value="events">
          {myEvents.length === 0 ? (
            <Card className="border-[#E5E0D5]">
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-[#6B665E] mb-4" />
                <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">No events created yet</h3>
                <p className="text-[#6B665E] font-['Nunito'] mb-4">Create your first event using the button above.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {upcomingMyEvents.length > 0 && (
                <>
                  <h2 className="font-['Nunito'] text-sm font-semibold text-[#6B665E] uppercase tracking-wide">Upcoming</h2>
                  {upcomingMyEvents.map(event => <EventCard key={event.event_id} event={event} />)}
                </>
              )}
              {pastMyEvents.length > 0 && (
                <>
                  <h2 className="font-['Nunito'] text-sm font-semibold text-[#6B665E] uppercase tracking-wide mt-6">Past</h2>
                  {pastMyEvents.map(event => <EventCard key={event.event_id} event={event} />)}
                </>
              )}
            </div>
          )}
        </TabsContent>

        {/* RSVPs Tab */}
        <TabsContent value="rsvps">
          <RsvpsTab
            upcomingRsvps={upcomingRsvps}
            pastRsvps={pastRsvps}
            navigate={navigate}
            RsvpCard={RsvpCard}
          />
        </TabsContent>
      </Tabs>

      {/* Cancel RSVP Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-white border-[#E5E0D5]">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">Cancel RSVP?</DialogTitle>
            <DialogDescription className="font-['Nunito'] text-[#6B665E]">
              Are you sure you want to cancel your RSVP for "{selectedRsvp?.event_title}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="border-[#E5E0D5] font-['Nunito']">
              Keep RSVP
            </Button>
            <Button onClick={handleCancelRsvp} disabled={canceling} className="bg-red-600 hover:bg-red-700 text-white font-['Nunito']" data-testid="confirm-cancel-rsvp">
              {canceling ? "Canceling..." : "Yes, Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Event Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-[#E5E0D5]">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">Delete Event?</DialogTitle>
            <DialogDescription className="font-['Nunito'] text-[#6B665E]">
              Are you sure you want to delete "{selectedEvent?.title}"? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-[#E5E0D5] font-['Nunito']">
              Cancel
            </Button>
            <Button onClick={handleDeleteEvent} disabled={deletingEvent} className="bg-red-600 hover:bg-red-700 text-white font-['Nunito']" data-testid="confirm-delete-event">
              {deletingEvent ? "Deleting..." : "Yes, Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
